import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchClientEditOptions, type ClientEditOptions } from '@/api/clients';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';
import { useSession } from '@/hooks/useSession';
import { showToast } from '@/lib/toast';
import { WizardStepper } from '@/components/WizardStepper';
import { StepCompanyInfo } from './StepCompanyInfo';
import { StepPersonalData } from './StepPersonalData';
import { StepBalance } from './StepBalance';
import { StepTests } from './StepTests';
import { StepPrices } from './StepPrices';
import { StepReview } from './StepReview';
import { useSubmitWizard } from './useSubmitWizard';
import {
  createInitialWizardState,
  validateWizardStep,
  REQUIRED_FIELD_LABELS,
  WIZARD_STEP_LABELS,
  type WizardState,
} from './wizardState';

const LAST_STEP = WIZARD_STEP_LABELS.length;

/**
 * Мастер создания клиента — шесть шагов, состояние живёт целиком здесь и
 * передаётся шагам как пропсы (см. wizardState.ts). До клика "Создать" на
 * последнем шаге ничего не уходит на бэк: шаги 4-5 (тесты/цены) выбирают из
 * отдельного, не привязанного к клиенту каталога (см. api/testCatalog.ts) —
 * реальный account id появляется только после создания. Сама отправка —
 * useSubmitWizard.ts: сперва POST /clients, затем последовательно
 * updateClientTest() для каждого отмеченного теста/цены.
 */
export function ClientCreatePage() {
  const navigate = useNavigate();
  const [state, setState] = useState<WizardState>(createInitialWizardState);
  const [step, setStep] = useState(1);
  const [maxReached, setMaxReached] = useState(1);
  const [attemptedNext, setAttemptedNext] = useState(false);
  const [editOptions, setEditOptions] = useState<ClientEditOptions | null>(null);
  const { submitting, error, submit } = useSubmitWizard();
  const { refreshPortfolioSize } = useSession();

  useEffect(() => {
    fetchClientEditOptions()
      .then((options) => {
        setEditOptions(options);
        // По умолчанию куратор — сам создающий клиента (как и на бэке для
        // fallback, если tutorIds вообще не передать, см. handleTutors() в
        // Laravel) — тут просто предзаполняем то же значение в форме, чтобы
        // не пришлось выбирать себя вручную. Только пока список кураторов
        // ещё не тронут — не перезатираем осознанный выбор пользователя.
        setState((prev) => (prev.tutorIds.length === 0 ? { ...prev, tutorIds: [options.currentUserId] } : prev));
      })
      .catch(() => showToast('Не удалось загрузить справочники (кураторы, шаблоны соглашений).'));
  }, []);

  const missingFields = validateWizardStep(step, state);
  const fieldErrors = attemptedNext ? missingFields : [];

  const goToStep = (target: number) => {
    if (target > maxReached) return;
    setAttemptedNext(false);
    setStep(target);
  };

  const goNext = () => {
    if (missingFields.length > 0) {
      setAttemptedNext(true);
      return;
    }
    setAttemptedNext(false);
    const next = Math.min(step + 1, LAST_STEP);
    setStep(next);
    setMaxReached((m) => Math.max(m, next));
  };

  const goBack = () => {
    setAttemptedNext(false);
    setStep((s) => Math.max(1, s - 1));
  };

  const handleCreate = async () => {
    const created = await submit(state);
    if (created) {
      refreshPortfolioSize();
      navigate(`/clients/${created.id}`);
    }
  };

  return (
    <section className="detail">
      <Link to="/clients" className="detail__back">
        <Icon name="chevronLeft" size={14} /> Все клиенты
      </Link>

      <header className="detail__header">
        <h1>Создать клиента</h1>
      </header>

      <div className="card wizard">
        {submitting && (
          <div className="wizard__submitting-overlay" aria-live="polite">
            <Spinner label="Создание клиента…" />
            <p className="wizard__submitting-hint">
              Может занять до минуты — идёт регистрация в смежных системах. Не закрывайте страницу.
            </p>
          </div>
        )}

        <WizardStepper steps={WIZARD_STEP_LABELS} current={step} maxReached={maxReached} onStepClick={goToStep} />

        <div className="wizard__body">
          {attemptedNext && missingFields.length > 0 && (
            <p className="field-error">
              Заполните обязательные поля: {missingFields.map((k) => REQUIRED_FIELD_LABELS[k]).join(', ')}.
            </p>
          )}

          {step === 1 && (
            <StepCompanyInfo state={state} onChange={setState} editOptions={editOptions} fieldErrors={fieldErrors} />
          )}
          {step === 2 && (
            <StepPersonalData
              state={state}
              onChange={setState}
              editOptions={editOptions}
              fieldErrors={fieldErrors}
            />
          )}
          {step === 3 && <StepBalance state={state} onChange={setState} />}
          {step === 4 && <StepTests state={state} onChange={setState} />}
          {step === 5 && <StepPrices state={state} onChange={setState} />}
          {step === 6 && <StepReview state={state} editOptions={editOptions} />}

          {error && <p className="field-error">{error}</p>}
        </div>

        <div className="wizard__footer">
          <button type="button" className="btn btn--secondary" onClick={goBack} disabled={step === 1 || submitting}>
            <Icon name="chevronLeft" size={14} /> Назад
          </button>
          {step < LAST_STEP ? (
            <button type="button" className="btn btn--primary" onClick={goNext}>
              Далее <Icon name="chevronRight" size={14} />
            </button>
          ) : (
            <button type="button" className="btn btn--primary" onClick={handleCreate} disabled={submitting}>
              <Icon name="check" size={15} /> {submitting ? 'Создание…' : 'Создать'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
